import { parseNativeGameFrame } from 'shared/lib/tv-game/nativeFrame.ts';
import type { IcePairType } from 'shared/lib/tv-game/types.ts';

/** Parsed game frame from the DataChannel (same shape as `parseNativeGameFrame` elsewhere). */
export type WebRtcGameFrame = NonNullable<ReturnType<typeof parseNativeGameFrame>>;

/** `?debugWebrtc=1` on tv.html or `localStorage.setItem('debugWebrtc','1')` — log raw JSON from DataChannel only. */
export function isDebugWebrtc(): boolean {
  if (typeof window === 'undefined') return false;
  try {
    if (window.localStorage.getItem('debugWebrtc') === '1') return true;
  } catch {
    /* ignore */
  }
  try {
    return new URLSearchParams(window.location.search).get('debugWebrtc') === '1';
  } catch {
    return false;
  }
}

/** Fallback when `window.__GROOVER_ICE_SERVERS__` is unset (e.g. static hosting without injection). */
const DEFAULT_ICE_SERVERS: RTCIceServer[] = [
  { urls: 'stun:stun.l.google.com:19302' },
  { urls: 'stun:stun1.l.google.com:19302' },
];

/**
 * ICE list: `window.__GROOVER_ICE_SERVERS__` set by `/tv` HTML from server `.env` replaces defaults
 * entirely (see `docs/webrtc-ice-configuration.md`). Manual override before `tv.js` is still supported.
 */
function getIceServers(): RTCIceServer[] {
  try {
    const g = globalThis as unknown as { __GROOVER_ICE_SERVERS__?: RTCIceServer[] };
    const fromPage = g.__GROOVER_ICE_SERVERS__;
    if (Array.isArray(fromPage) && fromPage.length > 0) {
      return fromPage;
    }
  } catch {
    /* ignore */
  }
  return DEFAULT_ICE_SERVERS;
}

export type WebRTCState = 'idle' | 'connecting' | 'connected' | 'failed' | 'closed';

/**
 * One WebRTC peer connection per connected Swift player.
 * The TV is the *answerer* — the Swift app sends the offer.
 *
 * Data flows:   Swift app  ──[RTCDataChannel]──>  TV browser
 *               (P2P, no server after handshake)
 */
/** ICE candidate-pair stats (names differ slightly across browsers). */
function extractRttMsFromStats(report: RTCStatsReport): number | null {
  let best: number | null = null;
  report.forEach((raw) => {
    const s = raw as Record<string, unknown>;
    const type = s['type'];
    if (type !== 'candidate-pair') return;
    const nominated = s['nominated'] === true;
    const state = s['state'];
    const ok = nominated || state === 'succeeded' || state === 'in-progress';
    if (!ok) return;
    const rtt = s['currentRoundTripTime'];
    if (typeof rtt !== 'number' || rtt <= 0) return;
    const ms = rtt * 1000;
    if (best === null || ms < best) best = ms;
  });
  return best;
}

function extractTransportKindFromStats(report: RTCStatsReport): 'TURN' | 'STUN' | null {
  let selectedPair: Record<string, unknown> | null = null;
  const byId = new Map<string, Record<string, unknown>>();

  report.forEach((raw) => {
    const s = raw as Record<string, unknown>;
    const id = s['id'];
    if (typeof id === 'string') byId.set(id, s);
  });

  report.forEach((raw) => {
    const s = raw as Record<string, unknown>;
    const type = s['type'];
    if (type !== 'candidate-pair') return;
    const nominated = s['nominated'] === true;
    const selected = s['selected'] === true;
    const state = s['state'];
    const ok = nominated || selected || state === 'succeeded' || state === 'in-progress';
    if (ok && selectedPair === null) selectedPair = s;
  });

  if (!selectedPair) return null;

  const localId = selectedPair['localCandidateId'];
  const remoteId = selectedPair['remoteCandidateId'];
  const local = typeof localId === 'string' ? byId.get(localId) : undefined;
  const remote = typeof remoteId === 'string' ? byId.get(remoteId) : undefined;
  const localType = local?.['candidateType'];
  const remoteType = remote?.['candidateType'];

  if (localType === 'relay' || remoteType === 'relay') return 'TURN';
  if (
    localType === 'host' ||
    localType === 'srflx' ||
    localType === 'prflx' ||
    remoteType === 'host' ||
    remoteType === 'srflx' ||
    remoteType === 'prflx'
  ) {
    return 'STUN';
  }
  return null;
}

/** Selected ICE pair: worst candidate type of both sides + its RTT. */
export type IcePairInfo = {
  pair: IcePairType | null;
  rttMs: number | null;
  /** TV → TURN transport (`udp` / `tcp` / `tls`) when TV's side is `relay`; null otherwise. */
  relayProtocol: string | null;
};

const PAIR_RANK: Record<IcePairType, number> = { host: 0, prflx: 1, srflx: 2, relay: 3 };

function asIcePairType(v: unknown): IcePairType | null {
  return v === 'host' || v === 'srflx' || v === 'prflx' || v === 'relay' ? v : null;
}

/** LAN / link-local address (RFC 1918, 169.254/16, IPv6 ULA fc00::/7, link-local fe80::/10, mDNS `*.local`). */
function isPrivateAddress(address: string): boolean {
  const a = address.toLowerCase();
  if (a.endsWith('.local')) return true;
  const v4 = a.match(/^(\d+)\.(\d+)\.\d+\.\d+$/);
  if (v4) {
    const o1 = Number(v4[1]);
    const o2 = Number(v4[2]);
    return o1 === 10 || (o1 === 172 && o2 >= 16 && o2 <= 31) || (o1 === 192 && o2 === 168) || (o1 === 169 && o2 === 254);
  }
  return /^f[cd][0-9a-f]{2}:/.test(a) || /^fe[89ab][0-9a-f]:/.test(a);
}

/**
 * Тип кандидата для вердикта. Удалённый host-кандидат телефона часто виден ТВ как `prflx`
 * (проверка связности пришла раньше, чем кандидат по сигналингу) — с приватным адресом
 * это та же локальная сеть, поэтому считаем его `host`.
 */
function candidateTypeOf(candidate: Record<string, unknown> | undefined): IcePairType | null {
  const type = asIcePairType(candidate?.['candidateType']);
  if (type !== 'prflx') return type;
  const address = candidate?.['address'] ?? candidate?.['ip'];
  return typeof address === 'string' && isPrivateAddress(address) ? 'host' : type;
}

/**
 * `transport.selectedCandidatePairId` → `candidate-pair` (fallback: `nominated && succeeded`)
 * → local/remote `candidateType`. Host candidates may carry an mDNS address (`*.local`) — still `host`.
 */
function extractIcePairFromStats(report: RTCStatsReport): IcePairInfo | null {
  const byId = new Map<string, Record<string, unknown>>();
  let selectedPairId: string | null = null;
  report.forEach((raw) => {
    const s = raw as Record<string, unknown>;
    const id = s['id'];
    if (typeof id === 'string') byId.set(id, s);
    if (s['type'] === 'transport' && typeof s['selectedCandidatePairId'] === 'string') {
      selectedPairId = s['selectedCandidatePairId'];
    }
  });

  let pair = selectedPairId !== null ? byId.get(selectedPairId) : undefined;
  if (!pair) {
    report.forEach((raw) => {
      const s = raw as Record<string, unknown>;
      if (pair || s['type'] !== 'candidate-pair') return;
      if (s['nominated'] === true && s['state'] === 'succeeded') pair = s;
    });
  }
  if (!pair) return null;

  const localId = pair['localCandidateId'];
  const remoteId = pair['remoteCandidateId'];
  const localStats = typeof localId === 'string' ? byId.get(localId) : undefined;
  const local = candidateTypeOf(localStats);
  const remote = candidateTypeOf(typeof remoteId === 'string' ? byId.get(remoteId) : undefined);
  const rtt = pair['currentRoundTripTime'];
  const rttMs = typeof rtt === 'number' && rtt > 0 ? rtt * 1000 : null;
  const relay = localStats?.['relayProtocol'];
  const relayProtocol = typeof relay === 'string' ? relay : null;
  if (local === null || remote === null) return { pair: null, rttMs, relayProtocol };
  return { pair: PAIR_RANK[local] >= PAIR_RANK[remote] ? local : remote, rttMs, relayProtocol };
}

export type WebRtcMediaDiagnostics = {
  /** Inbound video bytes received (cumulative). 0 = no media packets ever arrived. */
  videoBytesReceived: number;
  /** Inbound video packets received (cumulative). */
  videoPacketsReceived: number;
  /** Frames decoded by the browser's video decoder. 0 with bytes>0 = codec mismatch. */
  videoFramesDecoded: number;
  /** Frames per second decoded. */
  videoFramesPerSecond: number | null;
  /** Codec used for inbound video (e.g. "video/H264", "video/VP8"). */
  videoCodec: string | null;
  /** Average jitter buffer delay in ms (sum/emitted). Realtime target: <60ms. */
  jitterBufferAvgMs: number | null;
  /** Current jitter buffer target in ms (what the receiver is aiming for). */
  jitterBufferTargetMs: number | null;
  /** Frames dropped before decode (jitter buffer overruns / late arrivals). */
  framesDropped: number;
  /** Frames received but never decoded. */
  framesReceived: number;
  /** Packet jitter (RFC 3550) in ms — network instability. */
  jitterMs: number | null;
  /** Packets lost (cumulative). */
  packetsLost: number;
  /** Frame width × height most recently decoded. */
  frameWidth: number | null;
  frameHeight: number | null;
  /** DataChannel bytes received (cumulative). */
  dcBytesReceived: number;
  /** DataChannel messages received (cumulative). */
  dcMessagesReceived: number;
  /** DTLS / connection state — "connected" means the secure channel is up. */
  connectionState: RTCPeerConnectionState;
  iceConnectionState: RTCIceConnectionState;
};

function extractMediaDiagnosticsFromStats(
  report: RTCStatsReport,
  pc: RTCPeerConnection,
): WebRtcMediaDiagnostics {
  const out: WebRtcMediaDiagnostics = {
    videoBytesReceived: 0,
    videoPacketsReceived: 0,
    videoFramesDecoded: 0,
    videoFramesPerSecond: null,
    videoCodec: null,
    jitterBufferAvgMs: null,
    jitterBufferTargetMs: null,
    framesDropped: 0,
    framesReceived: 0,
    jitterMs: null,
    packetsLost: 0,
    frameWidth: null,
    frameHeight: null,
    dcBytesReceived: 0,
    dcMessagesReceived: 0,
    connectionState: pc.connectionState,
    iceConnectionState: pc.iceConnectionState,
  };

  let videoCodecId: string | null = null;
  const codecsById = new Map<string, Record<string, unknown>>();

  report.forEach((raw) => {
    const s = raw as Record<string, unknown>;
    const type = s['type'];
    if (type === 'codec') {
      const id = typeof s['id'] === 'string' ? (s['id'] as string) : null;
      if (id) codecsById.set(id, s);
      return;
    }
    if (type === 'inbound-rtp' && s['kind'] === 'video') {
      const bytes = s['bytesReceived'];
      const pkts = s['packetsReceived'];
      const frames = s['framesDecoded'];
      const fps = s['framesPerSecond'];
      if (typeof bytes === 'number') out.videoBytesReceived = bytes;
      if (typeof pkts === 'number') out.videoPacketsReceived = pkts;
      if (typeof frames === 'number') out.videoFramesDecoded = frames;
      if (typeof fps === 'number') out.videoFramesPerSecond = fps;
      const jbDelay = s['jitterBufferDelay'];
      const jbEmitted = s['jitterBufferEmittedCount'];
      if (typeof jbDelay === 'number' && typeof jbEmitted === 'number' && jbEmitted > 0) {
        out.jitterBufferAvgMs = (jbDelay / jbEmitted) * 1000;
      }
      const jbTarget = s['jitterBufferTarget'];
      if (typeof jbTarget === 'number') out.jitterBufferTargetMs = jbTarget * 1000;
      const fDropped = s['framesDropped'];
      const fReceived = s['framesReceived'];
      if (typeof fDropped === 'number') out.framesDropped = fDropped;
      if (typeof fReceived === 'number') out.framesReceived = fReceived;
      const jitter = s['jitter'];
      if (typeof jitter === 'number') out.jitterMs = jitter * 1000;
      const lost = s['packetsLost'];
      if (typeof lost === 'number') out.packetsLost = lost;
      const fw = s['frameWidth'];
      const fh = s['frameHeight'];
      if (typeof fw === 'number') out.frameWidth = fw;
      if (typeof fh === 'number') out.frameHeight = fh;
      const codecId = s['codecId'];
      if (typeof codecId === 'string') videoCodecId = codecId;
      return;
    }
    if (type === 'data-channel') {
      const bytes = s['bytesReceived'];
      const msgs = s['messagesReceived'];
      if (typeof bytes === 'number') out.dcBytesReceived += bytes;
      if (typeof msgs === 'number') out.dcMessagesReceived += msgs;
    }
  });

  if (videoCodecId) {
    const codec = codecsById.get(videoCodecId);
    const mime = codec?.['mimeType'];
    if (typeof mime === 'string') out.videoCodec = mime;
  }

  return out;
}

/**
 * After setRemoteDescription, force VP8 first on all incoming video transceivers.
 *
 * Reason: cheap Android TV / Tizen hardware H.264 decoders frequently fail to
 * decode the H.264 profile/level chosen by native senders (we observed
 * `framesDecoded=0` while `bytesReceived` and `packetsReceived` grew normally).
 * VP8 is software-decoded by Chromium everywhere and works on every WebRTC
 * stack. This call has no effect if the remote SDP only offers H.264 — in that
 * case the native sender must add VP8 to its own codec list (see
 * docs/webrtc-prefer-vp8.md).
 */
type CodecCap = {
  mimeType?: string;
  clockRate?: number;
  channels?: number;
  sdpFmtpLine?: string;
};

function preferVp8OnVideoTransceivers(pc: RTCPeerConnection): void {
  type TransceiverWithSetCodec = RTCRtpTransceiver & {
    setCodecPreferences?: (codecs: CodecCap[]) => void;
  };
  type ReceiverStatic = typeof RTCRtpReceiver & {
    getCapabilities?: (kind: 'video' | 'audio') => { codecs: CodecCap[] } | null;
  };

  try {
    const ReceiverCls = RTCRtpReceiver as ReceiverStatic;
    const caps = ReceiverCls.getCapabilities?.('video');
    if (!caps || !Array.isArray(caps.codecs) || caps.codecs.length === 0) return;

    const isVideoCodec = (c: CodecCap): boolean =>
      typeof c.mimeType === 'string' && c.mimeType.toLowerCase().startsWith('video/');
    const isVp8 = (c: CodecCap): boolean =>
      typeof c.mimeType === 'string' && c.mimeType.toLowerCase() === 'video/vp8';
    const isRtxOrFec = (c: CodecCap): boolean => {
      const m = (c.mimeType ?? '').toLowerCase();
      return m === 'video/rtx' || m === 'video/red' || m === 'video/ulpfec' || m === 'video/flexfec-03';
    };

    const vp8 = caps.codecs.filter(isVp8);
    const rest = caps.codecs.filter((c) => isVideoCodec(c) && !isVp8(c) && !isRtxOrFec(c));
    const support = caps.codecs.filter(isRtxOrFec);
    const ordered = [...vp8, ...rest, ...support];
    if (vp8.length === 0) return;

    const transceivers = pc.getTransceivers();
    for (const t of transceivers) {
      const kind = t.receiver?.track?.kind;
      const mid = t.mid ?? '';
      if (kind !== 'video' && !mid.startsWith('video') && !mid.includes('1')) continue;
      const tx = t as TransceiverWithSetCodec;
      if (typeof tx.setCodecPreferences !== 'function') continue;
      try {
        tx.setCodecPreferences(ordered);
        console.log('[WebRTC][TV] codec preference: VP8 first', `mid=${mid}`);
      } catch (err) {
        console.warn('[WebRTC][TV] setCodecPreferences failed', err);
      }
    }
  } catch (err) {
    console.warn('[WebRTC][TV] preferVp8OnVideoTransceivers failed', err);
  }
}

/**
 * Push the receiver toward realtime playout. Two knobs:
 *   - jitterBufferTarget: caps how much the WebRTC stack pre-buffers before
 *     handing a frame to the <video> element. Default is hundreds of ms; for
 *     a live camera feed we want ~50ms.
 *   - playoutDelayHint: same idea, older Chromium API. Set in seconds.
 * Both are progressive enhancements — feature-detect and ignore failures.
 */
function tuneReceiverForLowLatency(receiver: RTCRtpReceiver, track: MediaStreamTrack): void {
  if (track.kind !== 'video') return;
  type TunableReceiver = RTCRtpReceiver & {
    jitterBufferTarget?: number | null;
    playoutDelayHint?: number | null;
  };
  const r = receiver as TunableReceiver;
  try {
    if ('jitterBufferTarget' in r) {
      r.jitterBufferTarget = 50;
      console.log('[WebRTC][TV] jitterBufferTarget=50ms');
    }
  } catch (err) {
    console.warn('[WebRTC][TV] set jitterBufferTarget failed', err);
  }
  try {
    if ('playoutDelayHint' in r) {
      r.playoutDelayHint = 0.05;
      console.log('[WebRTC][TV] playoutDelayHint=0.05s');
    }
  } catch (err) {
    console.warn('[WebRTC][TV] set playoutDelayHint failed', err);
  }
}

function summarizeLocalIceCandidate(candidate: RTCIceCandidateInit): {
  candidateType: string | null;
  protocol: string | null;
  address: string | null;
  sdpMid: string | null;
} {
  const raw = candidate.candidate;
  const candidateTypeMatch = typeof raw === 'string' ? raw.match(/\styp\s([a-zA-Z0-9_-]+)/) : null;
  const protocolMatch = typeof raw === 'string' ? raw.match(/(?:^|\s)(?:a=)?candidate:\S+\s+\d+\s+([a-zA-Z]+)\s+\d+\s+(\S+)/) : null;
  return {
    candidateType: candidateTypeMatch?.[1] ?? null,
    protocol: protocolMatch?.[1]?.toLowerCase() ?? null,
    address: protocolMatch?.[2] ?? null,
    sdpMid: candidate.sdpMid ?? null,
  };
}

export type WebRTCPeerOptions = {
  /** Every DataChannel message (before JSON parse) — for TV diagnostics (msg/s). */
  onChannelRawMessage?: () => void;
  /** Parsed and accepted native game frame from DataChannel JSON. */
  onParsedNativeFrame?: () => void;
  /** JSON parse failed or payload shape is unsupported for native frame parser. */
  onParseError?: () => void;
  /** Selected ICE candidate pair changed after connect. */
  onSelectedPairChange?: () => void;
  /** Remote media stream from native sender (video track path). */
  onRemoteStream?: (stream: MediaStream) => void;
};

export class WebRTCPeer {
  private pc: RTCPeerConnection;
  private state: WebRTCState = 'idle';
  private remoteDescriptionSet = false;
  private readonly pendingIceCandidates: RTCIceCandidateInit[] = [];
  private localIceCandidatesEmitted = 0;
  private localIceWatchdogId: number | null = null;

  onNativeFrame?: (frame: WebRtcGameFrame) => void;
  /** Fired with the raw parsed JSON when parseNativeGameFrame returns null (e.g. runner frames). */
  onUnknownFrame?: (raw: unknown) => void;
  onStateChange?: (state: WebRTCState) => void;
  /** Emitted when a local ICE candidate is gathered — send to Swift via signaling server */
  onIceCandidate?: (candidate: RTCIceCandidateInit) => void;

  constructor(
    readonly playerId: string,
    private readonly opts?: WebRTCPeerOptions,
  ) {
    this.pc = new RTCPeerConnection({ iceServers: getIceServers() });
    this.wireEvents();
  }

  /** Step 1 — TV receives the offer from Swift via signaling */
  async receiveOffer(sdp: RTCSessionDescriptionInit): Promise<RTCSessionDescriptionInit> {
    // Повторный offer на живом соединении (ICE restart / renegotiation): DataChannel уже открыт
    // и `connected` второй раз не придёт — состояние не сбрасываем и watchdog не заводим.
    const renegotiating = this.state === 'connected';
    if (!renegotiating) this.setState('connecting');
    this.remoteDescriptionSet = false;
    this.pendingIceCandidates.length = 0;
    this.localIceCandidatesEmitted = 0;
    if (this.localIceWatchdogId !== null) {
      window.clearTimeout(this.localIceWatchdogId);
      this.localIceWatchdogId = null;
    }
    console.log(
      '[WebRTC][TV] receive offer',
      this.playerId,
      `signaling=${this.pc.signalingState}`,
      `ice=${this.pc.iceConnectionState}`,
      `conn=${this.pc.connectionState}`,
    );
    // Candidates embedded in the offer (non-trickle) never reach addIceCandidate.
    for (const line of sdp.sdp?.match(/^a=candidate:.*$/gm) ?? []) this.logRemoteCandidate({ candidate: line }, 'sdp');
    await this.pc.setRemoteDescription(new RTCSessionDescription(sdp));
    this.remoteDescriptionSet = true;
    this.flushPendingIceCandidates();
    preferVp8OnVideoTransceivers(this.pc);
    const answer = await this.pc.createAnswer();
    await this.pc.setLocalDescription(answer);
    console.log(
      '[WebRTC][TV] answer sent',
      this.playerId,
      `signaling=${this.pc.signalingState}`,
      `ice=${this.pc.iceConnectionState}`,
      `conn=${this.pc.connectionState}`,
      `gather=${this.pc.iceGatheringState}`,
    );
    if (renegotiating) return answer;
    this.localIceWatchdogId = window.setTimeout(() => {
      if (this.localIceCandidatesEmitted > 0) return;
      console.warn(
        '[WebRTC][TV] no local ICE candidates emitted yet',
        this.playerId,
        `gather=${this.pc.iceGatheringState}`,
        `ice=${this.pc.iceConnectionState}`,
        `conn=${this.pc.connectionState}`,
      );
      // TV WebRTC stack can hang in gathering without producing candidates at all.
      // Mark as failed early so UI does not keep user on a long "connecting" spinner.
      this.setState('failed');
    }, 3000);
    return answer;
  }

  /** Step 3 — TV receives ICE candidates from Swift */
  addIceCandidate(candidate: RTCIceCandidateInit): void {
    this.logRemoteCandidate(candidate, 'trickle');
    // Android sends 127.0.0.1 / ::1: a check to loopback fails on send, and older Chrome / WebView
    // then drop the whole UDP socket (host, srflx, UDP TURN) — only TCP TURN is left.
    const address = summarizeLocalIceCandidate(candidate).address;
    if (address && (/^127\./.test(address) || address === '::1')) return;
    if (!this.remoteDescriptionSet) {
      this.pendingIceCandidates.push(candidate);
      return;
    }
    void this.pc.addIceCandidate(new RTCIceCandidate(candidate)).catch((err) => {
      console.warn('[WebRTC][TV] addIceCandidate failed', this.playerId, err);
    });
  }

  private logRemoteCandidate(candidate: RTCIceCandidateInit, via: 'sdp' | 'trickle'): void {
    const info = summarizeLocalIceCandidate(candidate);
    console.log(
      '[WebRTC][TV] remote ice candidate',
      this.playerId,
      `via=${via}`,
      `type=${info.candidateType ?? '-'}`,
      `proto=${info.protocol ?? '-'}`,
      `addr=${info.address ?? '-'}`,
    );
  }

  /** Offer от того же RTCPeerConnection телефона (DTLS fingerprint совпадает), а не от нового. */
  isSameRemoteSession(sdp: RTCSessionDescriptionInit): boolean {
    const current = this.pc.remoteDescription?.sdp;
    if (!current) return true;
    const fingerprint = (s: string | undefined) => s?.match(/^a=fingerprint:.+$/m)?.[0].trim() ?? null;
    return fingerprint(current) === fingerprint(sdp.sdp);
  }

  close(): void {
    if (this.localIceWatchdogId !== null) {
      window.clearTimeout(this.localIceWatchdogId);
      this.localIceWatchdogId = null;
    }
    this.pc.close();
    this.setState('closed');
  }

  getState(): WebRTCState {
    return this.state;
  }

  /**
   * ICE round-trip time from getStats (nominated / active candidate pair).
   * Not all browsers fill `currentRoundTripTime` immediately — may return null.
   */
  async getRoundTripTimeMs(): Promise<number | null> {
    try {
      const report = await this.pc.getStats();
      return extractRttMsFromStats(report);
    } catch {
      return null;
    }
  }

  async getTransportKind(): Promise<'TURN' | 'STUN' | null> {
    try {
      const report = await this.pc.getStats();
      return extractTransportKindFromStats(report);
    } catch {
      return null;
    }
  }

  /** Every candidate pair with its check counters — shows whether checks to the phone get answers. */
  private async logCandidatePairs(reason: string): Promise<void> {
    try {
      const report = await this.pc.getStats();
      const byId = new Map<string, Record<string, unknown>>();
      report.forEach((raw) => {
        const s = raw as Record<string, unknown>;
        if (typeof s['id'] === 'string') byId.set(s['id'], s);
      });
      const describe = (id: unknown): string => {
        const c = typeof id === 'string' ? byId.get(id) : undefined;
        if (!c) return '-';
        return `${c['candidateType'] ?? '-'}/${c['protocol'] ?? '-'}/${c['address'] ?? c['ip'] ?? '-'}:${c['port'] ?? '-'}`;
      };
      report.forEach((raw) => {
        const s = raw as Record<string, unknown>;
        if (s['type'] !== 'candidate-pair') return;
        console.log(
          '[WebRTC][TV] ice pair',
          this.playerId,
          `reason=${reason}`,
          `local=${describe(s['localCandidateId'])}`,
          `remote=${describe(s['remoteCandidateId'])}`,
          `state=${s['state'] ?? '-'}`,
          `nominated=${s['nominated'] === true}`,
          `req=${s['requestsSent'] ?? '-'}/${s['responsesReceived'] ?? '-'}`,
          `inReq=${s['requestsReceived'] ?? '-'}/${s['responsesSent'] ?? '-'}`,
        );
      });
    } catch (err) {
      console.warn('[WebRTC][TV] ice pair stats failed', this.playerId, err);
    }
  }

  async getIcePair(): Promise<IcePairInfo | null> {
    try {
      const report = await this.pc.getStats();
      return extractIcePairFromStats(report);
    } catch {
      return null;
    }
  }

  async getMediaDiagnostics(): Promise<WebRtcMediaDiagnostics | null> {
    try {
      const report = await this.pc.getStats();
      return extractMediaDiagnosticsFromStats(report, this.pc);
    } catch {
      return null;
    }
  }

  private wireEvents(): void {
    // Swift creates the DataChannel → TV receives it here
    this.pc.ondatachannel = (event) => {
      const channel = event.channel;
      channel.onopen = () => {
        this.setState('connected');
        // Pair may switch after connect (e.g. relay → host); not every TV browser exposes this.
        const iceTransport = this.pc.sctp?.transport?.iceTransport;
        iceTransport?.addEventListener?.('selectedcandidatepairchange', () => {
          this.opts?.onSelectedPairChange?.();
        });
      };
      channel.onclose = () => {
        this.setState('closed');
      };
      channel.onmessage = (e) => {
        this.opts?.onChannelRawMessage?.();
        const dataStr = typeof e.data === 'string' ? e.data : String(e.data);
        /** Defer parse so a burst of DataChannel messages does not monopolize one task / block rAF. */
        queueMicrotask(() => {
          try {
            const raw: unknown = JSON.parse(dataStr);
            const frame = parseNativeGameFrame(raw);
            // Target Attack HUD (`currentLevel`, `levelProgress`, …) берётся только из этого JSON.
            // Если Kotlin шлёт по P2P укороченное тело без этих полей, парсер их не «додумает» — нужно слать
            // тот же объект, что и в Socket `GameMessage` / `game:frame`.
            // Сырой лог только при ?debugWebrtc=1 или localStorage.debugWebrtc=1
            if (isDebugWebrtc()) {
              const preview =
                dataStr.length > 4000 ? `${dataStr.slice(0, 4000)}… (${dataStr.length} chars)` : dataStr;
              console.log('[WebRTC] raw JSON ← DataChannel', preview);
              if (!frame) {
                console.warn('[WebRTC] parseNativeGameFrame returned null (unsupported shape?)');
              }
            }
            const rawType = raw && typeof raw === 'object' ? (raw as Record<string, unknown>)['type'] : undefined;
            const isRunnerFrame = typeof rawType === 'string' && rawType.includes('RunnerData');
            const isIqFrame = typeof rawType === 'string' && rawType.includes('IqTouchAnswer');
            const isBirdFlightFrame = typeof rawType === 'string' && rawType.includes('BirdFlightData');
            const isChefMasterFrame = typeof rawType === 'string' && rawType.includes('ChefMasterData');
            const isFruitSliceWebFrame = rawType === 'FruitSliceWebData';
            const isSkyRaceFrame = typeof rawType === 'string' && rawType.includes('SkyRaceData');
            const isLaserDodgeFrame = typeof rawType === 'string' && rawType.includes('LaserDodgeData');
            const isIqWebFrame = typeof rawType === 'string' && rawType.includes('IqData');
            // Headless: общий кадр позы, без привязки к игре — мимо parseNativeGameFrame.
            const isBodyPoseFrame = typeof rawType === 'string' && rawType.includes('BodyPoseData');
            if (isRunnerFrame || isIqFrame || isBirdFlightFrame || isChefMasterFrame || isFruitSliceWebFrame || isSkyRaceFrame || isLaserDodgeFrame || isIqWebFrame || isBodyPoseFrame) {
              this.opts?.onParsedNativeFrame?.();
              this.onUnknownFrame?.(raw);
            } else if (frame) {
              this.opts?.onParsedNativeFrame?.();
              this.onNativeFrame?.(frame);
            } else {
              this.opts?.onParseError?.();
              this.onUnknownFrame?.(raw);
            }
          } catch (err) {
            this.opts?.onParseError?.();
            if (isDebugWebrtc()) {
              console.warn('[WebRTC] JSON parse failed', err, dataStr.slice(0, 500));
            }
          }
        });
      };
    };

    this.pc.ontrack = (event) => {
      console.log(
        '[WebRTC][TV] ontrack',
        this.playerId,
        `kind=${event.track.kind}`,
        `streams=${event.streams.length}`,
      );
      tuneReceiverForLowLatency(event.receiver, event.track);
      const stream = event.streams[0] ?? new MediaStream([event.track]);
      this.opts?.onRemoteStream?.(stream);
    };

    this.pc.onicecandidate = (event) => {
      if (event.candidate) {
        this.localIceCandidatesEmitted += 1;
        const candidate = event.candidate.toJSON();
        const info = summarizeLocalIceCandidate(candidate);
        // Not in the SDP string; `RTCIceCandidate.relayProtocol` is missing from TS lib.dom.
        const relayProtocol = (event.candidate as { relayProtocol?: string | null }).relayProtocol ?? null;
        console.log(
          '[WebRTC][TV] local ice candidate',
          this.playerId,
          `type=${info.candidateType ?? '-'}`,
          `proto=${info.protocol ?? '-'}`,
          `addr=${info.address ?? '-'}`,
          `relay=${relayProtocol ?? '-'}`,
          `mid=${info.sdpMid ?? '-'}`,
        );
        this.onIceCandidate?.(candidate);
      } else {
        console.log('[WebRTC][TV] local ice gathering finished', this.playerId);
      }
    };

    this.pc.onconnectionstatechange = () => {
      console.log(
        '[WebRTC][TV] connectionstate',
        this.playerId,
        `state=${this.pc.connectionState}`,
        `signaling=${this.pc.signalingState}`,
        `ice=${this.pc.iceConnectionState}`,
      );
      switch (this.pc.connectionState) {
        case 'connected':
          this.setState('connected');
          window.setTimeout(() => void this.logCandidatePairs('connected+1s'), 1000);
          break;
        case 'failed':
          void this.logCandidatePairs('failed');
          this.setState('failed');
          break;
        case 'closed':      this.setState('closed');    break;
        case 'disconnected':
          // Temporary ICE loss — browser will retry automatically.
          // Keep the peer in the map so the QR-reset timer does not fire.
          break;
      }
    };

    this.pc.onsignalingstatechange = () => {
      console.log('[WebRTC][TV] signalingstate', this.playerId, `state=${this.pc.signalingState}`);
    };

    this.pc.oniceconnectionstatechange = () => {
      console.log('[WebRTC][TV] icestate', this.playerId, `state=${this.pc.iceConnectionState}`);
    };

    this.pc.onicegatheringstatechange = () => {
      console.log('[WebRTC][TV] icegathering', this.playerId, `state=${this.pc.iceGatheringState}`);
    };

    this.pc.onicecandidateerror = (event) => {
      const hostCandidate = (event as RTCPeerConnectionIceErrorEvent & { hostCandidate?: string })
        .hostCandidate;
      console.warn(
        '[WebRTC][TV] icecandidateerror',
        this.playerId,
        `url=${event.url ?? '-'}`,
        `host=${hostCandidate ?? '-'}`,
        `errorCode=${event.errorCode ?? '-'}`,
        `errorText=${event.errorText ?? '-'}`,
      );
    };
  }

  private setState(s: WebRTCState): void {
    // После close() поздние события (DataChannel onclose, ICE watchdog) не должны долетать наружу.
    if (this.state === 'closed') return;
    this.state = s;
    this.onStateChange?.(s);
  }

  private flushPendingIceCandidates(): void {
    if (!this.remoteDescriptionSet || this.pendingIceCandidates.length === 0) return;
    const batch = this.pendingIceCandidates.splice(0, this.pendingIceCandidates.length);
    batch.forEach((candidate) => {
      void this.pc.addIceCandidate(new RTCIceCandidate(candidate)).catch((err) => {
        console.warn('[WebRTC][TV] addIceCandidate failed (queued)', this.playerId, err);
      });
    });
  }
}

/**
 * Manages multiple WebRTC peers (one per connected Swift player).
 */
export class WebRTCManager {
  private readonly peers = new Map<string, WebRTCPeer>();
  private readonly remoteStreams = new Map<string, MediaStream>();
  private dataChannelRawMessageCount = 0;
  private parseErrorCount = 0;
  private lastParsedNativeFrameAtMs: number | null = null;

  onNativeFrame?: (playerId: string, frame: WebRtcGameFrame) => void;
  onUnknownFrame?: (playerId: string, raw: unknown) => void;
  onIceCandidate?: (playerId: string, candidate: RTCIceCandidateInit) => void;
  onPeerStateChange?: (playerId: string, state: WebRTCState) => void;
  onRemoteStream?: (playerId: string, stream: MediaStream | null) => void;
  onSelectedPairChange?: (playerId: string) => void;

  async handleOffer(
    playerId: string,
    sdp: RTCSessionDescriptionInit,
  ): Promise<RTCSessionDescriptionInit> {
    let peer = this.peers.get(playerId);
    // Телефон пересоздал RTCPeerConnection (после ICE failed) — старый DTLS/SCTP не переиспользовать.
    if (peer && !peer.isSameRemoteSession(sdp)) {
      console.log('[WebRTC][TV] offer from a new peer connection — recreating peer', playerId);
      peer.close();
      peer = undefined;
    }
    if (!peer) {
      const created = new WebRTCPeer(playerId, {
        onChannelRawMessage: () => {
          this.dataChannelRawMessageCount += 1;
        },
        onParsedNativeFrame: () => {
          this.lastParsedNativeFrameAtMs = Date.now();
        },
        onParseError: () => {
          this.parseErrorCount += 1;
        },
        onRemoteStream: (stream) => {
          this.remoteStreams.set(playerId, stream);
          this.onRemoteStream?.(playerId, stream);
        },
        onSelectedPairChange: () => this.onSelectedPairChange?.(playerId),
      });
      peer = created;
      this.peers.set(playerId, created);

      created.onNativeFrame = (frame) => this.onNativeFrame?.(playerId, frame);
      created.onUnknownFrame = (raw) => this.onUnknownFrame?.(playerId, raw);
      created.onIceCandidate = (candidate) => this.onIceCandidate?.(playerId, candidate);
      created.onStateChange = (state) => {
        // Пир уже заменён новым (телефон пересоздал RTCPeerConnection) — его события не трогают новый.
        if (this.peers.get(playerId) !== created) return;
        this.onPeerStateChange?.(playerId, state);
        if (state === 'closed' || state === 'failed') {
          if (this.remoteStreams.has(playerId)) {
            this.remoteStreams.delete(playerId);
            this.onRemoteStream?.(playerId, null);
          }
          this.peers.delete(playerId);
        }
      };
    }

    return peer.receiveOffer(sdp);
  }

  closePeer(playerId: string): void {
    this.peers.get(playerId)?.close();
  }

  handleIceCandidate(playerId: string, candidate: RTCIceCandidateInit): void {
    this.peers.get(playerId)?.addIceCandidate(candidate);
  }

  /** P2P session ready — prefer DataChannel over duplicate `game:frame` from Socket.IO. */
  isPeerConnected(playerId: string): boolean {
    const p = this.peers.get(playerId);
    return p !== undefined && p.getState() === 'connected';
  }

  closeAll(): void {
    this.remoteStreams.forEach((_s, playerId) => {
      this.onRemoteStream?.(playerId, null);
    });
    this.remoteStreams.clear();
    this.peers.forEach((p) => p.close());
    this.peers.clear();
    this.lastParsedNativeFrameAtMs = null;
    this.parseErrorCount = 0;
    this.dataChannelRawMessageCount = 0;
  }

  getActiveCount(): number {
    let n = 0;
    this.peers.forEach((p) => {
      if (p.getState() === 'connected') n += 1;
    });
    return n;
  }

  /** Average RTT (ms) across connected peers; null if none or stats unavailable. */
  async getAverageRoundTripTimeMs(): Promise<number | null> {
    const connected: WebRTCPeer[] = [];
    this.peers.forEach((p) => {
      if (p.getState() === 'connected') connected.push(p);
    });
    if (connected.length === 0) return null;
    const samples = await Promise.all(connected.map((p) => p.getRoundTripTimeMs()));
    const nums = samples.filter((v): v is number => v !== null && Number.isFinite(v));
    if (nums.length === 0) return null;
    return nums.reduce((a, b) => a + b, 0) / nums.length;
  }

  /**
   * Route kind by selected ICE candidate pair:
   * `TURN` when any active pair uses `relay`, `STUN` for host/srflx/prflx, `null` if unknown.
   */
  async getTransportKind(): Promise<'TURN' | 'STUN' | 'MIXED' | null> {
    const connected: WebRTCPeer[] = [];
    this.peers.forEach((p) => {
      if (p.getState() === 'connected') connected.push(p);
    });
    if (connected.length === 0) return null;
    const kinds = await Promise.all(connected.map((p) => p.getTransportKind()));
    const set = new Set(kinds.filter((k): k is 'TURN' | 'STUN' => k !== null));
    if (set.size === 0) return null;
    if (set.size > 1) return 'MIXED';
    return set.has('TURN') ? 'TURN' : 'STUN';
  }

  /** Selected ICE pair of one connected peer; null if not connected or stats unavailable. */
  async getIcePair(playerId: string): Promise<IcePairInfo | null> {
    const p = this.peers.get(playerId);
    if (!p || p.getState() !== 'connected') return null;
    return p.getIcePair();
  }

  /** Any peer past idle (offer received). */
  getPeerCount(): number {
    return this.peers.size;
  }

  /** Aggregated media diagnostics across peers (first peer only — usually 1 native sender). */
  async getMediaDiagnostics(): Promise<WebRtcMediaDiagnostics | null> {
    const peers: WebRTCPeer[] = [];
    this.peers.forEach((p) => peers.push(p));
    if (peers.length === 0) return null;
    return peers[0].getMediaDiagnostics();
  }

  /**
   * Raw DataChannel messages since last call (use once per second for msg/s).
   * Counts before JSON parse; includes non-game payloads if any.
   */
  takeDataChannelMessagesSinceLastSample(): number {
    const n = this.dataChannelRawMessageCount;
    this.dataChannelRawMessageCount = 0;
    return n;
  }

  /** Parse errors since last sample (reset-on-read), useful for 1s diagnostics line. */
  takeParseErrorsSinceLastSample(): number {
    const n = this.parseErrorCount;
    this.parseErrorCount = 0;
    return n;
  }

  /** Milliseconds since last valid parsed native frame from DataChannel. */
  getLastParsedFrameAgeMs(): number | null {
    if (this.lastParsedNativeFrameAtMs === null) return null;
    return Math.max(0, Date.now() - this.lastParsedNativeFrameAtMs);
  }
}
